/**
 * Wait for the companion store to report `connected`, or time out.
 *
 * Used by [[companion-reconnect-action]] to verify that a successful
 * `companion_restart` IPC call actually translated into a re-established
 * WebSocket handshake with Django. The IPC layer returns the moment the
 * subprocess (or Rust engine task) is up — it does NOT know whether
 * Django accepted the new connection. Without this verify step, a user
 * with bad network / expired token would see "Reconnecting…"
 * forever and never get a real failure signal.
 *
 * Resolves `true` if `status` is observed as `"connected"` at any point
 * within `windowMs`; resolves `false` on timeout. Subscription is torn
 * down in both cases so callers don't have to clean up.
 */
import { useCompanionStore } from "@/stores/companion-store";

export function waitForCompanionConnected(windowMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    if (useCompanionStore.getState().status === "connected") {
      resolve(true);
      return;
    }
    let unsubscribe: () => void = () => {};
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(false);
    }, windowMs);
    unsubscribe = useCompanionStore.subscribe((state, prev) => {
      if (prev.status !== "connected" && state.status === "connected") {
        clearTimeout(timer);
        unsubscribe();
        resolve(true);
      }
    });
  });
}
